import type { ResearchRequest } from './researchQueue';

/**
 * The research status strip on Prospecting (Drop 237).
 *
 * Kyu wanted one place that says where Senku and Reigen's research stands:
 * what is waiting to go, what is out, what came back. One request covers both
 * of them (20270154000100), so it is one set of counts.
 *
 * Counted per PERSON, from each prospect's newest request. The queue keeps
 * history, so somebody researched in June and asked about again today has two
 * rows; counting rows would put them in Done and Queued at once, and the
 * numbers would stop adding up to the people on the page.
 *
 * STUCK is Dev 2's ask: a request that went out more than two days ago and has
 * nothing filed is not "in progress", and showing it under Sent would say it
 * was. Nothing requeues those automatically yet; that is the parked settling
 * sweep (docs/design-note-settling-the-night.md). The strip makes them visible
 * instead of silent.
 */

export type ResearchBucket = 'queued' | 'sent' | 'stuck' | 'done' | 'failed';

export const RESEARCH_BUCKETS: readonly { key: ResearchBucket; label: string; hint: string }[] = [
  { key: 'queued', label: 'Queued', hint: 'Goes out in tonight’s sheet' },
  { key: 'sent', label: 'Sent', hint: 'With Senku, under 2 days' },
  { key: 'stuck', label: 'Stuck', hint: 'Sent over 2 days ago, no answer' },
  { key: 'done', label: 'Done', hint: 'Research filed' },
  { key: 'failed', label: 'Failed', hint: 'Could not be researched' },
];

/**
 * Today's date as the DATABASE writes dates: UTC.
 *
 * `sent_on` is stamped with PostgreSQL `current_date` (20270155000100), and
 * production runs in UTC. The viewer is in Manila, eight hours ahead. Dev 4,
 * in review of the first version, which used the viewer's local date: a
 * request sent at 21:30 UTC on 22 September has sent_on 22 September, but it
 * is already 23 September in Manila, so at Manila midnight on the 25th it read
 * 25 - 22 = 3 and turned Stuck after 42.5 hours. Both sides of the subtraction
 * have to be the same calendar, and the database's is the one that is fixed.
 */
export function researchToday(now: Date): string {
  return now.toISOString().slice(0, 10);
}

/** More than this many days out with no answer, and a request is stuck. */
export const STUCK_AFTER_DAYS = 2;

export type ResearchCounts = Record<ResearchBucket, number>;

function dayNumber(isoDate: string): number {
  // Dates only, both on the UTC calendar: `sent_on` is the database's UTC
  // date and `today` comes from researchToday. A timestamp fallback is cut to
  // its UTC date the same way.
  return Math.floor(Date.parse(`${isoDate.slice(0, 10)}T00:00:00Z`) / 86_400_000);
}

/**
 * Which count one request belongs in. `today` is YYYY-MM-DD.
 *
 * A sent request with no `sent_on` falls back to the day it was requested:
 * older rows may lack the stamp, and treating them as sent today would hide
 * exactly the stale ones this count exists to show.
 */
export function researchBucket(request: ResearchRequest, today: string): ResearchBucket {
  if (request.state !== 'sent') return request.state;
  const since = request.sent_on ?? request.requested_at;
  return dayNumber(today) - dayNumber(since) > STUCK_AFTER_DAYS ? 'stuck' : 'sent';
}

/** Counts over the given people, from each one's newest request. */
export function researchCounts(
  latest: ReadonlyMap<string, ResearchRequest>,
  prospectIds: Iterable<string>,
  today: string,
): ResearchCounts {
  const counts: ResearchCounts = { queued: 0, sent: 0, stuck: 0, done: 0, failed: 0 };
  for (const id of prospectIds) {
    const request = latest.get(id);
    if (request !== undefined) counts[researchBucket(request, today)] += 1;
  }
  return counts;
}

/** Whether this person's newest request is in the picked count. */
export function inResearchBucket(
  latest: ReadonlyMap<string, ResearchRequest>,
  prospectId: string,
  bucket: ResearchBucket,
  today: string,
): boolean {
  const request = latest.get(prospectId);
  return request !== undefined && researchBucket(request, today) === bucket;
}
